import {
  Base,
  Component,
  Emit,
  Prop,
  Ref,
  Watch
} from '@/vue-property-decorator'
import { Bind, Debounce } from '@/utils/Decorator'
import { CommonCodeAuth } from '@/components/business-components'
import {
  CreateModeOptType,
  LoginRegistryConfigStatus,
  ModifyPromoteCurrencyStatus,
  NormalRegisterRequest,
  ThirdWayLoginReq
} from '@/api/common/type'
import {
  DIGIT_REG,
  EMOJI_REG,
  SPECIAL_SYMBOLS_REG,
  VI_VN_STRING
} from '@/config/regexp.config'
import { FormModel, Icon, Space } from '@/plugins/ant-design-vue/tsx-support'
import {
  FormModelAttrs,
  GeetCaptProps,
  LREvents,
  LoginRegisterName,
  LoginRegisterValue,
  RegisterFormState,
  RegisterFromProps,
  RuleAttrName,
  Themes
} from './types'
import { GeeTest } from '@/controller/GeeTest'
import { GlobalConst, GlobalEvents } from '@/context'
import { apiGetSystemStatus } from '@/api/common'
import { cloneDeep, isBoolean, isNumber, merge, pick } from 'lodash'
import { createPngSprite, createSvgSprite } from '@/utils/business-utils/assets'
import { getQueryString } from '@/utils/Tool'
import { onAppEvent } from '@/utils/business-utils'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import AutoShrinkText from '@/components/auto-shrink-text'
import FuncRender from '@/components/business-components/func-render'
import MyScroll from '@/components/my-scroll'
import PassStrengthCheck from '@/components/business-components/pass-strength-check'
import Rules from '@/controller/Rules'
import User from '@/controller/User'
import style from './form.module.scss'

@Component<RegisterFrom>({
  name: 'RegisterFrom'
})
export default class RegisterFrom extends Base<
  unknown,
  RegisterFromProps,
  LREvents
> {
  @Ref()
  private refFormModal!: Required<FormModelAttrs>
  @Ref()
  private refPhoneCode!: CommonCodeAuth
  @Ref()
  private refEmailCode!: CommonCodeAuth
  @Ref()
  private refScrollContainer!: HTMLDivElement

  @Prop()
  private showLabel!: RegisterFromProps['showLabel']
  @Prop()
  private type!: RegisterFromProps['type']
  @Prop()
  private thirdPartyOptions!: RegisterFromProps['thirdPartyOptions']
  @Prop()
  private defuForm!: RegisterFromProps['defuForm']
  @Prop()
  private currentPhoneNumberSegment!: RegisterFromProps['currentPhoneNumberSegment']
  @Prop()
  private defaultDisableUsername!: RegisterFromProps['defaultDisableUsername']
  @Prop()
  private defaultDisablePassword!: RegisterFromProps['defaultDisablePassword']
  @Prop()
  private defaultVisiblePassword!: RegisterFromProps['defaultVisiblePassword']

  /** 账号输入框是否禁用 */
  private disableUsername = true
  /** 密码输入框是否禁用 */
  private disablePassword = true
  /** 密码是否明文 */
  private visiblePassword = true
  /** 是否获取过手机验证码 */
  private phoneCodeHasBeenGot = false
  /** 是否获取过邮箱验证码 */
  private emailCodeHasBeenGot = false
  private registerLoading = false
  private keyupCacheUsernameKeys = ''

  private funcRender = FuncRender.createFormModelItem.call(this)

  form: RegisterFormState = {
    platformId: '',
    passwdConfirm: '',
    passwd: '',
    mobilePhoneNumber: '',
    verificationCode: '',
    email: '',
    emailCode: '',
    realName: '',
    cpfCode: '',
    currency: '',
    inviterId: ''
  }

  created() {
    this.disableUsername = this.defaultDisableUsername ?? this.disableUsername
    this.disablePassword = this.defaultDisablePassword ?? this.disablePassword
    this.visiblePassword = this.defaultVisiblePassword ?? this.visiblePassword

    this.form = merge(this.form, this.defuForm)

    /** 如果没有传入密码，则是需要手动填写密码的，默认不禁用密码输入框 */
    if (!this.defuForm?.passwd) {
      this.disablePassword = false
      this.visiblePassword = false
    }
  }

  @Emit('currentPhoneNumberSegment')
  private onCurrentPhoneNumberSegment(currentPhoneNumberSegment: string) {
    return currentPhoneNumberSegment
  }
  @Emit('registerSuccess')
  private onRegisterSuccess(userInfo: RegisterFormState) {
    return userInfo
  }
  @Watch('registerLoading')
  @Emit('loading')
  private onLoading() {
    return this.registerLoading
  }

  mounted() {
    this.openValidatorPassword()
    const { ipBindInfos } = useMainStore()
    if (ipBindInfos?.promoterUserIdx) {
      this.form.inviterId = String(ipBindInfos?.promoterUserIdx)
    }
    this.initDefaultCurrency()
  }

  protected initDefaultCurrency() {
    const { ipBindInfos, siteInfos, currentTryCurrency, ipCheck } =
      useMainStore()

    const currencyInfos = siteInfos?.currencyInfos ?? []
    if (!currencyInfos.length) {
      return
    }

    const findCurrency = [
      /** 渠道连接设置了币种 */
      ipBindInfos?.currency,
      /** 地址栏上带了币种 */
      this.agentPayload.currency as string,
      /** ip推荐币种 */
      ipCheck.recommendCurrency,
      /** 试玩币种 */
      currentTryCurrency?.currencyCode,
      /** 默认展示币种为列表第一个 */
      currencyInfos[0].currencyCode
    ]
      .filter(Boolean)
      .find((currencyCode) =>
        currencyInfos.find((item) => item.currencyCode === currencyCode)
      )

    if (findCurrency) {
      this.form.currency = findCurrency
    }
  }

  /** 初始时自动校验传入的密码是否符合校验规范，不符合自动打开编辑 */
  private async openValidatorPassword() {
    await new Promise((resolve) => {
      let deepTotal = 0
      const timer = setInterval(() => {
        deepTotal++
        /** 直到赋值了 formModel，或者超时5秒，跳过 */
        if (this.refFormModal || deepTotal > 10) {
          clearInterval(timer)
          resolve(null)
        }
      }, 500)
    })
    const password = this.defuForm?.passwd
    const rules = this.options.formOptions[this.type].password

    if (this.refFormModal && password && rules.required) {
      this.refFormModal.validateField('passwd', (errorMessage) => {
        if (errorMessage) {
          this.disablePassword = false
        }
      })
    }
  }

  private get placeholder(): RegisterFormState {
    const { homeLayoutInfos } = useMainStore()
    const { t } = useI18n()
    switch (Number(homeLayoutInfos?.commonConfig?.registerLogin)) {
      case Themes.Theme3:
        return {
          platformId: String(
            t('lobby.modal.login.placeholder.platformId3', { minMax: '4-16' })
          ),
          passwd: String(
            t('lobby.modal.login.placeholder.passwd3', { minMax: '4-16' })
          ),
          passwdConfirm: String(
            t('lobby.modal.login.placeholder.passwdConfirm3', {
              minMax: '4-16'
            })
          ),
          mobilePhoneNumber: String(
            t('lobby.modal.login.placeholder.mobilePhoneNumber')
          ),
          verificationCode: String(
            t('lobby.modal.login.placeholder.verificationCode3')
          ),
          email: String(t('lobby.modal.login.placeholder.email')),
          emailCode: String(t('lobby.modal.login.placeholder.emailCode3')),
          realName: this.options.realNameMustUppercase
            ? String(t('lobby.modal.login.placeholder.realName32'))
            : String(t('lobby.modal.login.placeholder.realName31')),
          cpfCode: 'CPF',
          currency: String(t('lobby.modal.login.placeholder.currency')),
          inviterId: String(t('lobby.modal.login.placeholder.inviterId3'))
        }
      case Themes.Theme2:
      case Themes.Theme1:
      default:
        return {
          platformId: String(t('lobby.modal.login.placeholder.platformId')),
          passwd: String(t('lobby.modal.login.placeholder.passwd')),
          passwdConfirm: String(
            t('lobby.modal.login.placeholder.passwdConfirm')
          ),
          mobilePhoneNumber: String(
            t('lobby.modal.login.placeholder.mobilePhoneNumber')
          ),
          verificationCode: String(
            t('lobby.modal.login.placeholder.verificationCode')
          ),
          email: String(t('lobby.modal.login.placeholder.email')),
          emailCode: String(t('lobby.modal.login.placeholder.emailCode')),
          realName: String(t('lobby.modal.login.placeholder.realName')),
          cpfCode: String(t('lobby.modal.login.placeholder.cpfCode')),
          currency: String(t('lobby.modal.login.placeholder.currency')),
          inviterId: String(t('lobby.modal.login.placeholder.inviterId'))
        }
    }
  }

  private get formatPhoneNumber() {
    if (this.form.mobilePhoneNumber && this.currentPhoneNumberSegment) {
      return `${this.currentPhoneNumberSegment}-${this.form.mobilePhoneNumber}`
    }
    return ''
  }

  private get layout() {
    if (!this.showLabel) return {}
    return {
      layout: 'horizontal',
      labelCol: { span: 5 },
      wrapperCol: { span: 19 }
    }
  }

  /** 获取 form 表单的实列 */
  public get formInstance() {
    return this.refFormModal
  }

  /** 第三方极验 */
  private async thirdWayRegisterValidate(forceGeetest?: boolean) {
    if (this.options.validateWithNothing && !forceGeetest) return
    if (GeeTest.checkGeetestRestricted()) return Promise.reject()

    const { systemInfos, uuid } = useMainStore()
    const captchaId = forceGeetest
      ? systemInfos?.geetest_captcha_id.force_geetest
      : systemInfos?.geetest_captcha_id.register
    const result = await GeeTest.initGeetest({
      geetestConfig: { captchaId: captchaId ?? '' }
    })

    if (result) {
      await GeeTest.validateGeetest({
        account: this.form.platformId || uuid, // 注册总是会有用户名，或自动生成，或用户输入
        ...merge(result, {
          captcha_id: result?.captcha_id || captchaId
        })
      })
    }
  }
  /** 极验 */
  private async registerValidate(opts: GeetCaptProps) {
    const { code, geetCaptchaId, forceGeetest } = opts
    const hasGeetTest =
      forceGeetest ?? GlobalConst.ServiceCode.FORCE_GEETEST === code
    if (hasGeetTest || geetCaptchaId) {
      if (GeeTest.checkGeetestRestricted()) {
        return Promise.reject()
      }
      const { systemInfos, uuid } = useMainStore()
      const captchaId =
        geetCaptchaId ??
        (hasGeetTest
          ? systemInfos?.geetest_captcha_id.force_geetest
          : systemInfos?.geetest_captcha_id.register) ??
        ''
      const result = await GeeTest.initGeetest({
        geetestConfig: { captchaId: captchaId }
      })

      if (result) {
        await GeeTest.validateGeetest({
          account: this.form.platformId || uuid, // 注册总是会有用户名，或自动生成，或用户输入
          ...merge(result, {
            captcha_id: result?.captcha_id || captchaId
          })
        })
      }
    }
  }

  private formatPayload() {
    const payload = cloneDeep(this.form)
    // 清理没有赋值的数据
    for (const key of Reflect.ownKeys(payload)) {
      if (!isBoolean(Reflect.get(payload, key)) && !Reflect.get(payload, key)) {
        Reflect.deleteProperty(payload, key)
      }
    }
    /** 无论是否配置了验证码输入框，均需要传 verificationCode 为必填项目 */
    // if (!payload.verificationCode) payload.verificationCode = '0000'
    /** 手机号需要补上区号 */
    if (this.formatPhoneNumber) {
      payload.mobilePhoneNumber = this.formatPhoneNumber
    }

    if (this.agentPayload.cid) {
      merge(payload, { agent_type: 1, agent_code: this.agentPayload.cid })
    } else if (this.agentPayload.id) {
      merge(payload, { agent_type: 2, agent_code: this.agentPayload.id })
    }

    /** 云控精聊推广参数 */
    if (this.agentPayload?.chtjid && this.agentPayload?.chtpf) {
      merge(payload, {
        chtpf: this.agentPayload.chtpf,
        chtjid: this.agentPayload.chtjid
      })
    }

    /**
     * 新旧字段兼容，在接口层会判断有取值 inviter_code 和 inviterId，最终会使用 inviterId 传参
     * 这里两个字段都传，不管用那个，都保证数据能到后端
     * 这里两个都显示赋值，好区分
     */
    if (payload.inviterId) {
      merge(payload, {
        inviterId: payload.inviterId,
        inviter_code: payload.inviterId
      })
    }

    switch (this.type) {
      case LoginRegisterName.Account:
        return { ...payload, registerType: LoginRegisterValue.Account }
      case LoginRegisterName.Phone:
        return { ...payload, registerType: LoginRegisterValue.Phone }
      case LoginRegisterName.Email:
        return { ...payload, registerType: LoginRegisterValue.Email }
      case LoginRegisterName.ThirdParty:
      default:
        return { ...payload, registerType: LoginRegisterValue.ThirdParty }
    }
  }

  @Bind()
  @Debounce(500)
  private autoScrollValidateErrorFormItem(_selector?: keyof RegisterFormState) {
    this.$nextTick(() => {
      let selector = '.has-error'
      if (_selector) {
        selector = `[data-validate-field='${_selector}']`
      }

      if (!this.refScrollContainer) return
      const targetElement = this.refScrollContainer.querySelector(selector)
      const errorFormItem = targetElement?.closest('.ant-space-item')
      // 有多个输入框的，优先取 ant-input，没有的才取input，兼容手机号有两个输入看的场景
      let inputElement = targetElement?.querySelector('input.ant-input')
      if (!inputElement) {
        inputElement = targetElement?.querySelector('input')
      }
      const suffixElement = targetElement?.querySelector('[data-click-suffix]')

      // 滚动到可视区域
      errorFormItem?.scrollIntoView({ block: 'center', inline: 'nearest' })
      // 输入框聚焦
      ;(inputElement as HTMLInputElement)?.focus()
      // 如果存在需要点击才能编辑的，自动点击一下
      // 用户名和密码存在默认禁用不可编辑的情况，先点击
      if (suffixElement) {
        ;(suffixElement as HTMLElement)?.click()
        this.$nextTick(() => {
          // 输入框重新聚焦
          ;(inputElement as HTMLInputElement)?.focus()
        })
      }
    })
  }

  private async validate() {
    return await new Promise((resolve, reject) => {
      this.formInstance.validate((succ, object) => {
        if (!succ) this.autoScrollValidateErrorFormItem()
        succ ? resolve(null) : reject(object)
      })
    })
  }

  public async submit(opts: GeetCaptProps = {}) {
    const { code, geetCaptchaId, forceGeetest } = opts

    /** 短信验证码获取过一次就不再校验没有获取过了 */
    if (this.phoneCodeHasBeenGot && this.refPhoneCode) {
      this.refPhoneCode.hasBeenGot = true
    }
    /** 邮箱验证码获取过一次就不再校验没有获取过了 */
    if (this.emailCodeHasBeenGot && this.refEmailCode) {
      this.refEmailCode.hasBeenGot = true
    }

    if (this.registerLoading) return
    await this.validate()
    this.registerLoading = true

    onAppEvent('registerClick')

    try {
      if (this.type === LoginRegisterName.ThirdParty) {
        await this.thirdWayRegisterValidate(forceGeetest)
        const payload = this.formatPayload() as NormalRegisterRequest
        await User.thirdWayRegister({
          ...payload,
          access_token: this.thirdPartyOptions!.access_token!,
          requester_type: this.thirdPartyOptions!.requester_type!
        } as ThirdWayLoginReq)
      } else {
        await this.registerValidate({ code, geetCaptchaId, forceGeetest })
        const payload = this.formatPayload() as NormalRegisterRequest
        await User.register(payload)
      }

      setTimeout(() => (this.registerLoading = false))

      onAppEvent('register')
      this.onRegisterSuccess(this.form)

      const { updateAutoFillData, updateIpBindInfo } = useMainStore()
      updateIpBindInfo(null)
      updateAutoFillData({
        username: this.form.platformId,
        userpass: this.form.passwd,
        email: this.form.email,
        phone: this.form.mobilePhoneNumber,
        phoneNumberSegment: this.currentPhoneNumberSegment
      })
    } catch (error) {
      this.registerLoading = false
      const err = error as { data: { code: number | undefined; msg: string } }
      const code = err?.data?.code
      const msg = err?.data?.msg
      const serviceCode = GlobalConst.ServiceCode

      /** 如果返回code等于拼图、图标、九宫格行为验证，就把captchaId传进去 */
      if (
        code === serviceCode.SWIPER_PUZZLE_GEETEST ||
        code === serviceCode.NINE_SQUARE_GEETEST ||
        code === serviceCode.GRAPHIC_CLICK_GEETEST
      ) {
        this.submit({ code, geetCaptchaId: msg })
      }
      if (code === serviceCode.FORCE_GEETEST) {
        /** 三方注册 */
        if (this.type === LoginRegisterName.ThirdParty) {
          this.submit({ forceGeetest: true })
        } else {
          this.submit({ code })
        }
      }
      if (code === serviceCode.CONFIG_CHANGE_ERR) {
        const { updateSystemInfos, osType } = useMainStore()
        const response = await apiGetSystemStatus({
          osType
        })
        const systemInfos = response!.data?.data?.homeGetSysInfo
        if (systemInfos) {
          updateSystemInfos(systemInfos)
          if (this.form.verificationCode) {
            this.form.verificationCode = ''
          }
          if (this.form.emailCode) {
            this.form.emailCode = ''
          }
        }
      }
      if (code === serviceCode.NAME_EXISTED_ERROR) {
        // 用户名已存在时，滚动到用户名输入框，且输入框聚焦
        this.autoScrollValidateErrorFormItem('platformId')
      }
    }
  }

  //真实姓名输入框后台有个连续输入长度限制，比如限制5，则连续输入字符超过5个后 需要输入一个空格才能继续输入
  private verifyUserName(val: string) {
    const { t } = useI18n()
    const leng = this.options.realNameConsecutiveCharLimitTimes as number
    let verify = true
    const errStr = t('lobby.finance.withdraw.add.inputMaxLengthExcessed', {
      leng
    }).toString()
    //把输入的字符串按‘ ’拆分开，每一组都裁剪0-5，然后再join拼接起来
    let arr = val.split(' ')
    arr = arr.map((item) => {
      if (item.length > leng) {
        verify = false
      }
      return item.substring(0, leng)
    })
    val = arr.join(' ')
    return { inputStr: val, verify, errStr }
  }

  protected get agentPayload() {
    return pick(this.$route.query, ['cid', 'id', 'chtpf', 'chtjid', 'currency'])
  }

  public get formOptions() {
    const { systemInfos, ipBindInfos } = useMainStore()
    const isFullFormDebug = getQueryString('isFullFormDebug')
    /**
     * 0 ==> 关闭
     * 1 ==> 开启 · 选填
     * 2 ==> 开启 · 必填
     * 3 ==> 开启 · 必填 · 验证码验证（手机号 / 邮箱）
     * 注意，对于一些场景，是存在冗余数据的，不要使用就好了，而且也没有使用的场景
     */
    function formatConfigRules(value?: LoginRegistryConfigStatus) {
      let visible = isNumber(value) && value >= LoginRegistryConfigStatus.OPEN
      if (isFullFormDebug) {
        visible = true
      }

      const required =
        isNumber(value) && value >= LoginRegistryConfigStatus.OPEN_REQUIRED
      const verify =
        isNumber(value) &&
        value >= LoginRegistryConfigStatus.OPEN_REQUIRED_VERIFY
      return { visible, required, verify }
    }

    const packageConfig = (attrName: RuleAttrName) => {
      const nameRegistry = Reflect.get(systemInfos || {}, attrName)
      let passwordConfirm = nameRegistry?.passwordConfirm
      /** 不是账号注册的，确认密码为 password 字段是否为3，为3就是显示必填，否则就是不显示 */
      if (attrName !== 'accountRegister') {
        passwordConfirm =
          nameRegistry?.password ===
          LoginRegistryConfigStatus.OPEN_REQUIRED_VERIFY
            ? LoginRegistryConfigStatus.OPEN_REQUIRED
            : LoginRegistryConfigStatus.CLOSE
      }
      return {
        account: formatConfigRules(
          nameRegistry?.account ?? LoginRegistryConfigStatus.OPEN_REQUIRED
        ),
        password: formatConfigRules(
          nameRegistry?.password ?? LoginRegistryConfigStatus.OPEN_REQUIRED
        ),
        passwordConfirm: formatConfigRules(passwordConfirm),
        phone: formatConfigRules(nameRegistry?.phone),
        email: formatConfigRules(nameRegistry?.email),
        realName: formatConfigRules(nameRegistry?.realName),
        cpf: formatConfigRules(
          this.form.currency === 'BRL'
            ? nameRegistry?.cpf
            : LoginRegistryConfigStatus.CLOSE
        ),
        currency: formatConfigRules(nameRegistry?.currency),
        invite: formatConfigRules(nameRegistry?.inviteCode),
        /** 自带邀请码不可修改，没有邀请码时可以改 */
        inviteCodeUnchangeable: !ipBindInfos?.promoterUserIdx
          ? { visible: false }
          : formatConfigRules(nameRegistry?.inviteCodeUnchangeable)
      }
    }
    return {
      account: packageConfig('accountRegister'),
      phone: packageConfig('phoneRegister'),
      email: packageConfig('emailRegister'),
      thirdParty: packageConfig('thirdPartyRegister')
    }
  }
  public get options() {
    const { systemInfos } = useMainStore()
    return {
      formOptions: this.formOptions,
      /** 真实姓名可以连续输入的字符数，超过后必须加空格才能继续 */
      realNameConsecutiveCharLimitTimes:
        systemInfos?.realNameConsecutiveCharLimitTimes,
      /** 真实姓名是否必须大写 */
      realNameMustUppercase: !!systemInfos?.realNameMustUppercase,
      /** 是否可以修改推广/渠道链接币种 */
      modifyPromoteCurrency:
        systemInfos?.modifyPromoteCurrency === ModifyPromoteCurrencyStatus.OPEN,
      /** 无需行为验证 */
      validateWithNothing: systemInfos?.register_validate_mode === 1
    }
  }

  private onUsernameBlur() {
    setTimeout(() => {
      if (!this.form.platformId) {
        GlobalEvents.dispatch({
          type: 'TRACE_REPORT',
          payload: {
            event: 'registerUsername',
            keyupCacheUsername: this.keyupCacheUsernameKeys
          }
        })
      }
      this.keyupCacheUsernameKeys = ''
    }, 2000)
  }

  private onUsernameKeyup(e: KeyboardEvent) {
    this.keyupCacheUsernameKeys += e.key
  }

  private fromItems() {
    const { t } = useI18n()
    const mainStore = useMainStore()
    const formOptions = this.options.formOptions[this.type]
    const { email, phone } = {
      [LoginRegisterName.Account]: {
        [LoginRegisterName.Phone]: true,
        [LoginRegisterName.Email]: true
      },
      [LoginRegisterName.Phone]: {
        [LoginRegisterName.Email]: true
      },
      [LoginRegisterName.Email]: {
        [LoginRegisterName.Phone]: true
      },
      [LoginRegisterName.ThirdParty]: {
        [LoginRegisterName.Phone]: true,
        [LoginRegisterName.Email]: true
      }
    }[this.type]

    return (
      <Space.Tsx
        ref="refScrollContainer"
        direction="vertical"
        size={0}
        class={['login-register-from-scroll']}
      >
        {[
          /** 账号 */
          Boolean(formOptions.account.visible) && [
            this.funcRender.username({
              prop: 'platformId',
              isRequired: formOptions.account.required && !this.form.platformId,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              beforeChange: (v: string) => v.replace(/\s/g, ''),
              editText: this.disableUsername,
              onEditText: () => (this.disableUsername = false),
              disabled: this.disableUsername,
              getRules: (rules) => {
                return [
                  {
                    ...rules.pattern,
                    required: formOptions.account.required
                  },
                  {
                    ...rules.required,
                    required: formOptions.account.required
                  }
                ]
              },
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.platformId
                },
                on: {
                  focus: () =>
                    this.autoScrollValidateErrorFormItem('platformId'),
                  blur: this.onUsernameBlur,
                  keyup: this.onUsernameKeyup
                }
              }
            }),
            this.type === LoginRegisterName.ThirdParty && (
              <span class="third-party-subtitle">
                {t('lobby.modal.register.editAccountTips')}
              </span>
            )
          ],
          /** 密码 */
          Boolean(formOptions.password.visible) &&
            this.funcRender.userpassCustomSuffix({
              prop: 'passwd',
              showEditBtn: this.disablePassword,
              visible: this.visiblePassword,
              onOpenEye: () => (this.visiblePassword = !this.visiblePassword),
              onEditText: () => (this.disablePassword = !this.disablePassword),
              isRequired: formOptions.password.required && !this.form.passwd,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.passwd
                },
                on: {
                  focus: () => this.autoScrollValidateErrorFormItem('passwd')
                }
              },
              getRules: (rules) => {
                return [
                  Rules.loginPwdSymbolCheck(),
                  {
                    ...rules.required,
                    required: formOptions.password.required
                  },
                  {
                    ...rules.pattern,
                    required: formOptions.password.required
                  },
                  {
                    validator: () => {
                      // 满足自身的校验规则之后， 如果当前修改的密码和确认密码都有，则触发一下确认密码的校验
                      // 形成两边修改都可以校验的效果
                      if (this.form.passwd && this.form.passwdConfirm) {
                        this.refFormModal.validateField(
                          'passwdConfirm',
                          () => null
                        )
                      }
                      return true
                    }
                  }
                ]
              },
              isReadConfig: true
            }),
          /** 密码强度 */
          Boolean(formOptions.password.visible && !this.disablePassword) && (
            <PassStrengthCheck password={this.form.passwd} />
          ),
          /** 确认密码 */
          Boolean(
            formOptions.passwordConfirm.visible && !this.disablePassword
          ) &&
            this.funcRender.userpass({
              prop: 'passwdConfirm',
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.passwdConfirm
                },
                on: {
                  focus: () =>
                    this.autoScrollValidateErrorFormItem('passwdConfirm')
                }
              },
              label: t('lobby.modal.register.passwdConfirm'),
              isRequired: !this.form.passwdConfirm,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              beforeChange: (v: string) => v.replace(/\s/g, ''),
              getRules: (rulesMap) => {
                return [
                  {
                    validator: (_rule, value, callback) => {
                      if (value !== this.form.passwd) {
                        callback(t('lobby.modal.register.notMatch') as string)
                      }
                      callback()
                      return true
                    }
                  },
                  rulesMap.required,
                  rulesMap.pattern
                ]
              },
              isReadConfig: true
            }),
          /** 手机号 */
          Boolean(phone && formOptions.phone.visible) &&
            this.funcRender.userPhoneSelect({
              prop: 'mobilePhoneNumber',
              isRequired:
                formOptions.phone.required && !this.form.mobilePhoneNumber,
              model: this.form,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              countryInfos: mainStore.countryInfos,
              defaultCountry: mainStore.defaultCountry || undefined,
              currentPhoneNumberSegment: this.currentPhoneNumberSegment,
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.mobilePhoneNumber
                },
                on: {
                  focus: () =>
                    this.autoScrollValidateErrorFormItem('mobilePhoneNumber')
                }
              },
              onSelectChange: (value: string) => {
                this.onCurrentPhoneNumberSegment(value)
                this.form.mobilePhoneNumber = ''
              },
              getRules: (rules) => {
                return [
                  { ...rules.pattern, required: formOptions.phone.required },
                  { ...rules.required, required: formOptions.phone.required }
                ]
              }
            }),
          /** 短信验证码 */
          Boolean(phone && formOptions.phone.verify) && (
            <CommonCodeAuth
              ref={'refPhoneCode'}
              prop={'verificationCode'}
              createMode={{ sms: CreateModeOptType.LoginRegister }}
              isRequired={formOptions.phone.required}
              showRequiredLabel={!this.showLabel && !this.form.verificationCode}
              showLabel={this.showLabel}
              hiddenPrefix={this.showLabel}
              hiddenIcon={this.showLabel}
              targetValue={this.formatPhoneNumber}
              mode="sms"
              v-model={this.form.verificationCode}
              placeholder={this.placeholder.verificationCode}
              onBeenGot={(beenGot) => {
                if (beenGot) {
                  this.phoneCodeHasBeenGot = beenGot
                }
              }}
              inputTsxData={{
                on: {
                  focus: () =>
                    this.autoScrollValidateErrorFormItem('verificationCode')
                }
              }}
            />
          ),
          /** 邮箱 */
          Boolean(email && formOptions.email.visible) &&
            this.funcRender.useremail({
              prop: 'email',
              isRequired: formOptions.email.required && !this.form.email,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              onSelectChange: () => {
                this.refFormModal.validateField('email', () => null)
              },
              selectTsxData: {
                placeholder: this.placeholder.email,
                on: {
                  focus: () => this.autoScrollValidateErrorFormItem('email')
                }
              },
              getRules: (rules) => {
                return [
                  { ...rules.pattern, required: formOptions.email.required },
                  { ...rules.required, required: formOptions.email.required }
                ]
              }
            }),
          /** 邮箱验证码 */
          Boolean(email && formOptions.email.verify) && (
            <CommonCodeAuth
              ref={'refEmailCode'}
              prop={'emailCode'}
              createMode={{ email: CreateModeOptType.LoginRegister }}
              showLabel={this.showLabel}
              hiddenPrefix={this.showLabel}
              hiddenIcon={this.showLabel}
              targetValue={this.form.email}
              mode={'email'}
              isRequired={formOptions.email.required}
              showRequiredLabel={!this.showLabel && !this.form.emailCode}
              v-model={this.form.emailCode}
              placeholder={this.placeholder.emailCode}
              onBeenGot={(beenGot) => {
                if (beenGot) {
                  this.emailCodeHasBeenGot = beenGot
                }
              }}
              inputTsxData={{
                on: {
                  focus: () => this.autoScrollValidateErrorFormItem('emailCode')
                }
              }}
            />
          ),
          /** 真实姓名 */
          Boolean(formOptions.realName.visible) &&
            this.funcRender.userRealName({
              prop: 'realName',
              isRequired: formOptions.realName.required && !this.form.realName,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              formModelItemTsxData: {
                props: {
                  help: () => {
                    return (
                      this.options.realNameMustUppercase &&
                      formOptions.realName.required && (
                        <div class="currency-help2">
                          {t('lobby.modal.register.realNameError')}
                        </div>
                      )
                    )
                  }
                }
              },
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.realName
                },
                on: {
                  blur: () => {
                    /**
                     * 去除首尾空格和多个空格过滤成1个空格
                     */
                    this.form.realName = this.form.realName
                      ?.trim()
                      .replace(/\s+/gi, ' ')
                  },
                  cut: (event: Event) => {
                    if (this.options.realNameMustUppercase) {
                      event.preventDefault()
                    }
                  },
                  copy: (event: Event) => {
                    if (this.options.realNameMustUppercase) {
                      event.preventDefault()
                    }
                  },
                  paste: (event: Event) => {
                    if (this.options.realNameMustUppercase) {
                      event.preventDefault()
                    }
                  },
                  focus: () => this.autoScrollValidateErrorFormItem('realName')
                }
              },
              getRules: (rules) => {
                this.form.realName = this.form.realName?.replace(/\s+/gi, ' ')
                if (formOptions.realName.required) {
                  return [
                    {
                      ...rules.pattern,
                      required: formOptions.realName.required
                    },
                    {
                      ...rules.required,
                      required: formOptions.realName.required
                    },
                    {
                      validator: (_rule, value, callback) => {
                        //必填情况下还要看自定义规则 开了自定义规则要走校验，
                        if (this.options.realNameConsecutiveCharLimitTimes) {
                          const { verify, inputStr, errStr } =
                            this.verifyUserName(value)
                          this.form.realName = inputStr
                          if (!verify) {
                            callback(errStr as string)
                            return false
                          }
                          callback()
                          return true
                        }
                        //没开自定义规则就走原本的校验规则
                        if (!value.trim() || value.length === 1) {
                          callback(
                            this.options.realNameMustUppercase
                              ? (t(
                                  'lobby.modal.login.form.realName.realNameTips3'
                                ) as string)
                              : (t(
                                  'lobby.modal.login.form.realName.realNameTips2'
                                ) as string)
                          )
                          return false
                        }
                        callback()
                        return true
                      }
                    }
                  ]
                }
                //没开必填情况下 如果开了自定义规则并且用户有输入，就要走自定义校验
                if (
                  this.form.realName &&
                  this.options.realNameConsecutiveCharLimitTimes
                ) {
                  return [
                    {
                      ...rules.pattern,
                      required: formOptions.realName.required
                    },
                    {
                      ...rules.required,
                      required: formOptions.realName.required
                    },
                    {
                      validator: (_rule, value, callback) => {
                        const { verify, inputStr, errStr } =
                          this.verifyUserName(value)
                        this.form.realName = inputStr
                        if (!verify) {
                          callback(errStr as string)
                          return false
                        }
                        callback()
                        return true
                      }
                    }
                  ]
                }
                //如果没开必填，并且用户没输入或者没开自定义校验，就不校验了
                return []
              },
              onChange: (event: Event) => {
                const target = event.target as unknown as {
                  value: string
                }
                /**
                 * 仅支持输入英文大写，特殊字符只能是空格和·
                 */
                if (this.options.realNameMustUppercase) {
                  /**
                   * 处理越南字符转换为大写英文
                   */
                  const valList = target.value.split('')
                  const result = valList
                    .map((val) => {
                      const newVal = val as keyof typeof VI_VN_STRING
                      if (VI_VN_STRING[newVal]) {
                        return VI_VN_STRING[newVal]
                      }
                      return val
                    })
                    .join('')
                  if (result) {
                    target.value = result
                  }
                  target.value = target.value
                    .replace(/[^a-zA-Z’‘'·,.\s]/g, '')
                    .toLocaleUpperCase()

                  this.form.realName = target.value
                } else {
                  /**
                   * 不允许输入特殊符号和表情(只允许·和单个空格)
                   */
                  target.value = target.value
                    .replace(SPECIAL_SYMBOLS_REG, '')
                    .replace(EMOJI_REG, '')
                    .replace(DIGIT_REG, '')
                  this.form.realName = target.value
                }
              }
            }),
          /** cpf */
          Boolean(formOptions.cpf.visible) &&
            this.funcRender.usercpf({
              prop: 'cpfCode',
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              isRequired: formOptions.cpf.required && !this.form.cpfCode,
              model: this.form,
              onChange: (e: Event) => {
                const target = e.target as unknown as { value: string }
                target.value = target.value.replace(/\D/g, '')
                this.form.cpfCode = target.value
              },
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.cpfCode
                },
                on: {
                  focus: () => this.autoScrollValidateErrorFormItem('cpfCode')
                }
              },
              getRules: (rules) => {
                if (formOptions.cpf.required) {
                  return [
                    { ...rules.required, required: true },
                    {
                      validator: (rule, value, callback) => {
                        if (value.length !== 11) {
                          callback(
                            t('lobby.finance.withdraw.warning.cpf').toString()
                          )
                          return false
                        }
                        callback()
                        return true
                      }
                    }
                  ]
                }
                if (this.form.cpfCode) {
                  return [
                    {
                      validator: (_rule, value, callback) => {
                        if (value && value.length !== 11) {
                          callback(
                            t('lobby.finance.withdraw.warning.cpf').toString()
                          )
                          return false
                        }
                        callback()
                        return true
                      }
                    }
                  ]
                }
                return []
              }
            }),
          /** 币种 */
          Boolean(formOptions.currency.visible) &&
            this.funcRender.userCurrencySelect({
              prop: 'currency',
              showLabel: this.showLabel,
              hiddenPrefix: true,
              hiddenIcon: this.showLabel,
              model: this.form,
              currencyInfos: mainStore.siteInfos?.currencyInfos ?? [],
              current: this.form.currency,
              disabled:
                (!!this.agentPayload.id || !!this.agentPayload.cid) &&
                !this.options.modifyPromoteCurrency &&
                !!mainStore.ipBindInfos?.currency,
              onSelectChange: (value: string) => {
                this.form.currency = value
                this.form.cpfCode = ''
                this.refFormModal.validateField('currency', () => null)
                this.$emit('setBenefitAmount', value)
              },
              selectTsxData: {
                placeholder: this.placeholder.currency
              },
              formModelItemTsxData: {
                props: {
                  help: () => {
                    /** 当邀请码存在时并且不是邀请链接注册，则显示help插槽 */
                    return (
                      this.form.currency && (
                        <div class="currency-help">
                          <AutoShrinkText
                            text={
                              t(
                                'lobby.modal.register.selectCountryError'
                              ) as string
                            }
                            rowCount={2}
                          ></AutoShrinkText>
                        </div>
                      )
                    )
                  }
                }
              }
            }),
          /** 邀请码 */
          Boolean(formOptions.invite.visible) &&
            this.funcRender.username({
              prop: 'inviterId',
              label: t('lobby.modal.register.inviterCode'),
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              // 新的邀请码对应的是用户id，不让输入其他内容
              beforeChange: (v: string) => v.replace(/\D/g, ''),
              model: this.form,
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.inviterId,
                  disabled: formOptions.inviteCodeUnchangeable.visible,
                  maxLength: 16
                },
                on: {
                  focus: () => this.autoScrollValidateErrorFormItem('inviterId')
                }
              },
              prefix: (
                <Icon.Tsx
                  slot="prefix"
                  component={{
                    functional: true,
                    render: () => {
                      return (
                        <div class="invite-prifix-wrap">
                          <icon-sprite
                            class="inviter-icon"
                            sprite={createSvgSprite('input_icon_yqm')}
                          />
                          {!this.showLabel &&
                            formOptions.invite.required &&
                            !this.form.inviterId && <span>*</span>}
                        </div>
                      )
                    }
                  }}
                />
              ),
              formModelItemTsxData: {
                props: {
                  help: () => {
                    /** 当邀请码存在时并且不是邀请链接注册，则显示help插槽 */
                    return (
                      this.form.inviterId &&
                      !this.agentPayload.id &&
                      !this.agentPayload.cid && (
                        <div class="inviter-currency-help">
                          <icon-sprite
                            sprite={createPngSprite(
                              'sprite_main_comm_icon_tip1'
                            )}
                            class="inviter-currency-help-before-mock"
                            scale={18 / 20}
                          />
                          {t('lobby.modal.register.selectCurrencyTips')}
                        </div>
                      )
                    )
                  }
                }
              },
              getRules: () => {
                return [
                  Rules.username(undefined, {
                    required: formOptions.invite.required,
                    message: () =>
                      formOptions.invite.required
                        ? t(
                            'lobby.modal.register.invitetAccountRequired'
                          ).toString()
                        : t('lobby.modal.register.rangeNumber', {
                            range: '4-16'
                          }).toString()
                  })
                ]
              }
            })
        ]}
      </Space.Tsx>
    )
  }

  render() {
    return (
      <FormModel.Tsx
        ref="refFormModal"
        props={{ model: this.form, ...this.layout }}
        class={style['login-register-from']}
      >
        <MyScroll>{this.fromItems()}</MyScroll>
      </FormModel.Tsx>
    )
  }
}
